import React, {useEffect, useMemo, useRef, useState} from "react";
import {fetchData} from "../../services/api";
import {Link, useNavigate, useParams} from "react-router-dom";
import styles from "./ProcessPaymentByCard.module.scss";
import Icon from "../../components/Icon";
import useVehiclesState from "../../model/useVehiclesState";
import useCardsState from "../../model/useCardsState";
import {t} from "i18next";
import useTokenSessionState from "../../model/useTokenSessionState";
import {paymentProcess, tokenPaymentProcess} from "../../model/helpers/Payment";
import FullscreenLoader from "../../components/FullscreenLoader/FullscreenLoader";
import useParkingSession from "../../model/useParkingSession";
import useBuyFormValues from "../../model/useBuyFormValues";
import useLocalStorageState from "../../hooks/useLocalStorageState";
import useEmailsState from "../../model/useEmailsState";

const ProcessPaymentByCard = (props) => {
    let paramsString = window.location.search;
    const searchParams = useMemo(() => new URLSearchParams(paramsString), [paramsString]);
    const navigate = useNavigate();

    const [caption, setCaption] = useState("paymentDone.caption.emailAndLicencePlateAndCard");

    const fetchInitiated = useRef(false);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [vehicleExist, setVehicleExist] = useState(false);
    const [cardExist, setCardExist] = useState(false);
    const [emailExist, setEmailExist] = useState(false);

    const [vehicleName, setVehicleName] = useState(null);
    const [cardName, setCardName] = useState(null);
    const [lastUsedEmail, setLastUsedEmail] = useLocalStorageState('buy_lastUsedEmail', '');

    const { getByIndexAsync: getVehicleByIndexAsync, add: addVehicle } = useVehiclesState();
    const { getByCardMaskedCLN: getCardByIndexAsync, add: addCard } = useCardsState();
    const { getByIndexAsync: getEmailByIndexAsync, add: addEmail } = useEmailsState();
    const { getByIndexAsync: getTokenSessionState } = useTokenSessionState();
    const { getLatestByDateTime: getBuyFormValuesLatest } = useBuyFormValues();
    const { add: addParkingSession, deleteByIndex: deleteParkingSession, getAll: getCurrentParkingSessions } = useParkingSession();
    const {token} = useParams();

    let interval = null;

    const setParkingSession = (ticketGUID, parkingFrom, parkingTo, ticketData) => {
        fetchData("StreetParking/sections/1", "GET", "url", {
            "sectionCode": ticketData.sectionCode
        }).then((sectionsData) => {
            let maxHour = null;

            if (sectionsData[0]) {
                maxHour = sectionsData[0].tariffInfo.maxHour;
            }

            let minutes = ticketData.acceptedMinutes;
            // Pokud se platí pro stejnou SPZ a zónu a existuje aktivní relace, tak ji smažeme, protože nová už je prodloužená o novou dobu
            getCurrentParkingSessions().then((sessions) =>
                sessions.map((session) => {
                    if (session.ticket.licensePlate === ticketData.licensePlate && session.ticket.sectionCode === ticketData.sectionCode) {
                        minutes += session.ticket.acceptedMinutes;
                        deleteParkingSession('id', session.id);
                    }
                })
            );

            addParkingSession({
                "guid": ticketGUID,
                "from": parkingFrom,
                "to": parkingTo,
                "ticket": ticketData,
                "maxHour": maxHour,
                "acceptedMinutes": minutes
            });

        });
    };

    useEffect( () => {

        // Payment canceled by user
        if (searchParams.get("paymentStatus") === "3" || searchParams.get("paymentStatus") === "6") {
            getBuyFormValuesLatest().then((latest) => {
                navigate(`/buy/${latest.featureType}/${latest.sectionCode}?getLatestValues=true`)
            });
        }

        if (props.zeroPay === true) {
            fetchData(`Tickets/${token}`, 'GET')
                .then((data) => {
                    setCardExist(true);
                    setPaymentInfo({ticket: data});
                    setParkingSession(data.ticketGUID, data.parkingFrom, data.parkingTo, data);
                });
        }

        if (props.applepay === true) {
            getTokenSessionState("uuid", token).then(session => {
                interval = setInterval(() => {
                    try {
                        paymentProcess('Applepay', session.ticketGUID).then(r => {
                            if (r.paymentSuccessful === true) {
                                clearInterval(interval);
                                setPaymentInfo(r);
                                setParkingSession(r.ticket.ticketGUID, r.ticket.parkingFrom, r.ticket.parkingTo, r.ticket);
                                setCardExist(true);
                            }
                        });
                    }catch (e) {
                        clearInterval(interval);
                        navigate(`/`);
                    }
                }, 2000);
            });
        }

        if (props.googlepay === true) {
            getTokenSessionState("uuid", token).then(session => {
                interval = setInterval(() => {
                    try {
                        paymentProcess('Googlepay', session.ticketGUID).then(r => {
                            if (r.paymentSuccessful === true) {
                                clearInterval(interval);
                                setPaymentInfo(r);
                                setParkingSession(r.ticket.ticketGUID, r.ticket.parkingFrom, r.ticket.parkingTo, r.ticket);
                                setCardExist(true);
                            }
                        });
                    }catch (e) {
                        clearInterval(interval);
                        navigate(`/`);
                    }
                }, 2000);
            });
        }

        if (props.token === true) {
            getTokenSessionState("uuid", token).then(session => {
                interval = setInterval(() => {
                    try {
                    tokenPaymentProcess(session.ticketGUID, session.paymentGWReference).then(r => {
                        if (r.paymentSuccessful === true) {
                            clearInterval(interval);
                            setPaymentInfo(r);
                            setParkingSession(r.ticket.ticketGUID, r.ticket.parkingFrom, r.ticket.parkingTo, r.ticket);
                        }
                    });
                    }catch (e) {
                        clearInterval(interval);
                        navigate(`/`);
                    }
                }, 2000);
            });
        } else {
            if (
                searchParams.has("payId") &&
                searchParams.has("dttm") &&
                searchParams.has("resultCode") &&
                searchParams.has("resultMessage") &&
                searchParams.has("paymentStatus") &&
                searchParams.has("authCode") &&
                searchParams.has("merchantData") &&
                !paymentInfo &&
                !fetchInitiated.current
            ) {
                fetchInitiated.current = true;
                fetchData("Payment/GatewayResult/Process", "POST", "body", searchParams.toString(), false).then((results) => {
                    if (results) {
                        setPaymentInfo(results);
                        setParkingSession(results.ticket.ticketGUID, results.ticket.parkingFrom, results.ticket.parkingTo, results.ticket);
                    } else {
                        if (fetchInitiated.current === false) {
                            navigate(`/`);
                        }
                    }
                });
            } else {
                if (fetchInitiated.current === true) navigate(`/`);
                /* Testovací parametry:
                setPaymentInfo({
                    "success": true,
                    "message": null,
                    "paymentGWReference": "tkn_e234454f89e84aecbbb01d002bdc1d06",
                    "paymentSuccessful": true,
                    "paymentGatewayOperationCancelled": false,
                    "paymentGatewayOperationDenied": false,
                    "isTokenPaidOrder": false,
                    "ticket": {
                        "ticketGUID": "e234454f89e84aecbbb01d002bdc1d06",
                        "dateCreated": "2024-02-29T10:38:51.417",
                        "priceTotal": 20,
                        "vat": 0,
                        "vatRate": 0,
                        "licensePlate": "fff",
                        "paymentStatus": "PAID",
                        "paymentDate": "2024-02-29T10:39:19.173",
                        "sectionCode": "P1-0276",
                        "parkingFrom": "2024-02-29T10:38:51.413",
                        "acceptedMinutes": 15,
                        "parkingTo": "2024-02-29T10:53:51.413",
                        "cardMaskedCLN": "1234",
                    }
                }); */
            }
        }
    }, [searchParams]);

    useEffect(() => {
        if (paymentInfo){
            if(props.token === true) setCardExist(true);
            if(props.zeroPay === true) setCardExist(true);

            getVehicleByIndexAsync("spz", paymentInfo.ticket.licensePlate)
               .then(vehicle => {
                   if (vehicle){
                       setVehicleExist(true);
                   }

                   if (lastUsedEmail) {
                          getEmailByIndexAsync("email", lastUsedEmail)
                            .then(email => {
                                if (email) {
                                    setEmailExist(true);
                                }
                            })
                   }

                   if (!props.token === true && !props.zeroPay === true) {
                       const card = getCardByIndexAsync(paymentInfo.ticket.cardMaskedCLN)

                       if (card) {
                           setCardExist(true);
                       }

                       if (vehicleExist && cardExist) navigate(`/paid/${paymentInfo.ticket.ticketGUID}`);
                   }
               })


        }
    }, [paymentInfo]);

    useEffect(() => {
        if (vehicleExist && cardExist && emailExist) navigate(`/paid/${paymentInfo.ticket.ticketGUID}`);
    }, [vehicleExist, cardExist, emailExist])

    useEffect(() => {
        if (!vehicleExist && !cardExist && !emailExist && lastUsedEmail.length) {
            setCaption("paymentDone.caption.emailAndLicencePlateAndCard");
        } else if (!vehicleExist && !cardExist) {
            setCaption("paymentDone.caption.licencePlateAndCard");
        } else if (!vehicleExist && !emailExist && lastUsedEmail.length) {
            setCaption("paymentDone.caption.emailAndLicencePlate");
        } else if (!emailExist && lastUsedEmail.length && !cardExist) {
            setCaption("paymentDone.caption.emailAndCard");
        } else if (!vehicleExist) {
            setCaption("paymentDone.caption.licencePlate");
        } else if (!cardExist) {
            setCaption("paymentDone.caption.card");
        } else if (!emailExist && lastUsedEmail.length) {
            setCaption("paymentDone.caption.email");
        }
    }, [vehicleExist, cardExist, emailExist]);

    const saveOnDevice = () => {
        if (!vehicleExist && vehicleName){
            addVehicle({
                "spz": paymentInfo.ticket.licensePlate,
                "name": vehicleName
            })
        }

        if (!cardExist){
            addCard({
                name: (cardName && cardName.length) ? cardName : `•••• ${paymentInfo.ticket.cardMaskedCLN.slice(-4)}`,
                token: paymentInfo.paymentGWReference,
                cardMaskedCLN: paymentInfo.ticket.cardMaskedCLN,
                cardExpiration: paymentInfo.ticket.cardExpiration,
            })
        }

        if (!emailExist && lastUsedEmail.length) {
            addEmail({
                email: lastUsedEmail
            })
        }

        navigate(`/paid/${paymentInfo.ticket.ticketGUID}`);
    }

    return (
        paymentInfo === null ?
        <div className={styles.Wrapper}>
            <FullscreenLoader />
        </div>
            :
        <div className={styles.containerWrapper}>
            <div className={styles.container}>
                <Link to={'/'} className={styles.back}>
                    <Icon.CaretLeft size={12} />
                    &nbsp;{t("paymentDone.leave")}
                </Link>
                <h3 className={styles.title}>{t("paymentDone.paymentSuccessful")}</h3>
                <p className={styles.caption}>{t(caption)}</p>
                {!vehicleExist && paymentInfo?.ticket?.licensePlate ?
                <div className={styles.item}>
                    <div className={styles.info}>
                        <div className={styles.icon}>
                            <Icon.Car size={22} color={"#1D4F8B"}/>
                        </div>
                        <div className={styles.title}>
                            <div className={styles.itemTitle}>
                                {t("paymentDone.car")}
                            </div>
                        </div>
                        <div className={styles.textMuted}>
                            {paymentInfo.ticket.licensePlate}
                        </div>
                        <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                            <input required={true} maxLength={22} onChange={(evt) => setVehicleName(evt.target.value)} placeholder={t("paymentDone.nameCar")}
                                   className={styles.input}/>
                        </div>
                    </div>
                </div> : "" }
                {!cardExist && !props.token === true && paymentInfo?.ticket?.cardMaskedCLN ?
                <div className={styles.item}>
                    <div className={styles.info}>
                        <div className={styles.icon}>
                            <Icon.PaymentCard size={22} color={"#1D4F8B"}/>
                        </div>
                        <div className={styles.title}>
                            <div className={styles.itemTitle}>
                                {t("paymentDone.card")}
                            </div>
                        </div>
                        <div className={styles.textMuted}>
                            •••• {paymentInfo.ticket.cardMaskedCLN.slice(-4)}
                        </div>
                        <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                            <input onChange={(evt) => setCardName(evt.target.value)} placeholder={t("paymentDone.nameCard")}
                                   className={styles.input}
                            maxLength={22}/>
                        </div>
                    </div>
                </div> : "" }
                {(!emailExist && lastUsedEmail.length) ?
                <div className={styles.item}>
                    <div className={styles.info}>
                        <div className={styles.icon}>
                            <Icon.Email size={22} color={"#1D4F8B"}/>
                        </div>
                        <div className={styles.title}>
                            <div className={styles.itemTitle}>
                                {t("paymentDone.email")}
                            </div>
                        </div>
                        <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                            <input required={true} readOnly={true} value={lastUsedEmail} onChange={(evt) => setLastUsedEmail(evt.target.value)} placeholder={t("paymentDone.email")}
                                   className={styles.input}
                            maxLength={22}/>
                        </div>
                    </div>
                    </div>
                    : "" }

                <div className={styles.buttons}>
                    <button className={styles.saveButton} onClick={() => saveOnDevice()}>
                        <Icon.SafeBadge className={styles.actionIcon} size={16}/>&nbsp;&nbsp;{t("paymentDone.saveOnThisDevice")}
                    </button>
                    <Link to={`/paid/${paymentInfo.ticket.ticketGUID}`} className={`${styles.saveButton} ${styles.skipButton}`}>
                        {t("paymentDone.skip")}&nbsp;<Icon.CaretRight className={styles.skipIcon} size={16}/>
                    </Link>
                </div>
            </div>
        </div>
    );
}
export default ProcessPaymentByCard;